import {
  PROTOCOL_VERSION,
  makeEnvelope,
  parseEnvelope,
  parseInitPayload
} from './protocol.ts'

export interface ChannelWindow {
  parent: { postMessage(message: unknown, targetOrigin: string): void }
  location?: { ancestorOrigins?: ArrayLike<string> }
  addEventListener(type: 'message', fn: (event: MessageEvent) => void): void
  removeEventListener(type: 'message', fn: (event: MessageEvent) => void): void
}

export interface ChannelHandlers {
  canInit?: () => boolean
  onPaymentInterrupted?: () => void
  onInit: (
    clientSecret: string,
    parentOrigin: string,
    generation: number
  ) => void
  onClose: () => void
}

export interface TerminalChannel {
  start: () => void
  stop: () => void
  post: (type: string, payload: Record<string, unknown>) => void
  resize: (height: number) => void
  markExchanged: (generation: number) => void
  generation: () => number
  parentOrigin: () => string | null
}

export interface TerminalChannelOptions {
  allowedOrigins: readonly string[]
}

/**
 * Creates the terminal side of the postMessage protocol.
 * @param win - The terminal's window, or a stand-in with the same shape.
 * @param handlers - Callbacks for accepted host messages.
 * @param options - The terminal's allowed parent origins. Any other origin gets no reply.
 * @returns The channel.
 */
export function createTerminalChannel(
  win: ChannelWindow,
  handlers: ChannelHandlers,
  options: TerminalChannelOptions
): TerminalChannel {
  const allowedOrigins = options.allowedOrigins
  let lockedOrigin: string | null = null
  let currentSecret: string | null = null
  let generation = 0
  let exchanged = false
  let lastResize: { generation: number; height: number } | null = null
  let latestHeight: number | null = null

  function send(
    type: string,
    payload: Record<string, unknown>,
    targetOrigin: string
  ): void {
    win.parent.postMessage(
      makeEnvelope(type, generation, payload),
      targetOrigin
    )
  }

  function sendResize(): void {
    if (lockedOrigin === null || latestHeight === null) return
    if (
      lastResize?.generation === generation &&
      lastResize.height === latestHeight
    )
      return
    lastResize = { generation, height: latestHeight }
    send('resize', { height: latestHeight }, lockedOrigin)
  }

  function onMessage(event: MessageEvent): void {
    if (event.source !== win.parent) return
    if (event.origin === 'null') return
    // A parent outside this terminal's list gets no reply, including version_unsupported.
    if (!allowedOrigins.includes(event.origin)) {
      return
    }
    if (lockedOrigin !== null && event.origin !== lockedOrigin) return

    const parsed = parseEnvelope(event.data)
    if (parsed.kind === 'ignore') return
    if (parsed.kind === 'unsupported_version') {
      send(
        'error',
        {
          code: 'version_unsupported',
          message: `Only protocol version ${PROTOCOL_VERSION} is supported.`
        },
        event.origin
      )
      return
    }

    const { type, payload } = parsed.envelope
    if (type === 'payment_interrupted' && currentSecret === null) {
      lockedOrigin = event.origin
      handlers.onPaymentInterrupted?.()
      return
    }
    if (type === 'init') {
      if (handlers.canInit?.() === false) return
      const secret = parseInitPayload(payload)
      if (secret === null || secret === currentSecret) return
      lockedOrigin = event.origin
      currentSecret = secret
      generation += 1
      exchanged = false
      // The first layout happens before the lock, so the host learns its height here.
      sendResize()
      handlers.onInit(secret, event.origin, generation)
      return
    }
    if (!exchanged) return
    if (type === 'close') handlers.onClose()
  }

  return {
    start() {
      win.addEventListener('message', onMessage)
      // The SDK waits for ready. The browser delivers it only to a matching parent origin.
      for (const origin of allowedOrigins) {
        send('ready', { supportedVersions: [PROTOCOL_VERSION] }, origin)
      }
    },
    stop() {
      win.removeEventListener('message', onMessage)
    },
    post(type, payload) {
      if (lockedOrigin === null) return
      send(type, payload, lockedOrigin)
    },
    resize(height) {
      latestHeight = height
      sendResize()
    },
    markExchanged(forGeneration) {
      if (forGeneration === generation) exchanged = true
    },
    generation: () => generation,
    parentOrigin: () => lockedOrigin
  }
}
